import { checkCreditDecision, CREDIT_NOTE_MAX, parseLimit, TERMS_OPTIONS, wholeDollars, type CreditDecisionForm } from "@flexfreight/core";
import {
  Button,
  DataTable,
  DefinitionList,
  FilterToolbar,
  KpiTile,
  Modal,
  notify,
  PageHeader,
  SearchInput,
  Select,
  StatusBadge,
  Tabs,
  TabPanel,
  TextAreaField,
  TextField,
  Truncate,
  sortRows,
  statusBadge,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../api/client";
import { ageDays, CustomerLink, day, emailOutcomeText, message, money, sortOf, sortPatch, time, useCan } from "./ops";

type Status = "pending" | "approved" | "rejected" | "withdrawn";
const STATUSES: Status[] = ["pending", "approved", "rejected", "withdrawn"];
export type CreditApplication = {
  id: string;
  orgId: string;
  orgName?: string;
  status: Status;
  legalName: string;
  requestedTermsDays: number;
  requestedLimitCents: number;
  monthlyVolumeCents: number | null;
  apEmail: string;
  tradeReferences: string | null;
  notes: string | null;
  decidedTermsDays: number | null;
  decidedLimitCents: number | null;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
};
export type CreditLimit = { orgId: string; orgName: string; termsDays: number | null; limitCents: number | null; openCents: number; availableCents: number | null };

const TABS = ["applications", "limits"] as const;
type Tab = (typeof TABS)[number];
const TAB_ID = "credit";
const FILTERS = { tab: "applications", status: "pending", q: "", sort: "", dir: "asc" };
const termsText = (d: number | null) => (d === null ? "Prepay" : `Net ${d}`);
const termOptions = TERMS_OPTIONS.map((t) => ({ value: String(t), label: `Net ${t}` }));

/** Freight > Credit: net-terms applications to decide, and every customer's limit, open balance and room left. */
export function CreditPage() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const tab: Tab = (TABS as readonly string[]).includes(v.tab) ? (v.tab as Tab) : "applications";
  const status: Status = STATUSES.includes(v.status as Status) ? (v.status as Status) : "pending";
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const apps = useQuery({
    queryKey: ["console", "credit", "applications", status],
    queryFn: () => api<{ applications: CreditApplication[] }>(`/v1/admin/credit-applications?${new URLSearchParams({ status })}`),
    placeholderData: keepPreviousData,
    enabled: tab === "applications",
  });
  const limits = useQuery({ queryKey: ["console", "credit", "limits"], queryFn: () => api<{ customers: CreditLimit[] }>("/v1/admin/credit-limits"), enabled: tab === "limits" });
  const [open, setOpen] = useState<CreditApplication | null>(null);
  const [editing, setEditing] = useState<CreditLimit | null>(null);
  const can = useCan();
  const needle = v.q.toLowerCase();
  const sort = sortOf(v);
  const appRows = sortRows(
    (apps.data?.applications ?? []).filter((a) => !needle || [a.orgName, a.legalName, a.apEmail].some((x) => x?.toLowerCase().includes(needle))),
    sort,
    (a, k) => (k === "applied" ? time(a.createdAt) : k === "customer" ? (a.orgName ?? "") : k === "legal" ? a.legalName : k === "asked" ? a.requestedLimitCents : k === "volume" ? a.monthlyVolumeCents : null),
  );
  const limitRows = sortRows(
    (limits.data?.customers ?? []).filter((r) => !needle || r.orgName.toLowerCase().includes(needle)),
    sort,
    (r, k) => (k === "customer" ? r.orgName : k === "limit" ? r.limitCents : k === "open" ? r.openCents : k === "left" ? r.availableCents : k === "terms" ? r.termsDays : null),
  );
  const appCols: Column<CreditApplication>[] = [
    { key: "applied", header: "Applied", sortable: true, width: "8.5rem", render: (a) => <>{day(a.createdAt)}<span className="ffa-block ffa-muted">{ageDays(a.createdAt)} days ago</span></> },
    { key: "customer", header: "Customer", sortable: true, truncate: "custom", render: (a) => <Truncate text={a.orgName ?? ""}><CustomerLink orgId={a.orgId} name={a.orgName ?? ""} /></Truncate> },
    { key: "legal", header: "Legal name", primary: true, sortable: true, render: (a) => <button type="button" className="ffa-linkbtn ffa-cell-strong" onClick={() => setOpen(a)}>{a.legalName}</button> },
    { key: "asked", header: "Asked for", numeric: true, sortable: true, width: "8.5rem", render: (a) => <>{wholeDollars(a.requestedLimitCents)}<span className="ffa-block ffa-muted">{termsText(a.requestedTermsDays)}</span></> },
    { key: "volume", header: "Monthly volume", numeric: true, sortable: true, width: "9.5rem", mobile: "hide", render: (a) => (a.monthlyVolumeCents === null ? "-" : wholeDollars(a.monthlyVolumeCents)) },
    { key: "status", header: "Status", width: "10rem", render: (a) => <StatusBadge domain="creditApplication" value={a.status} /> },
  ];
  const limitCols: Column<CreditLimit>[] = [
    { key: "customer", header: "Customer", primary: true, sortable: true, truncate: "custom", render: (r) => <Truncate text={r.orgName}><CustomerLink orgId={r.orgId} name={r.orgName} /></Truncate> },
    { key: "terms", header: "Terms", sortable: true, width: "6.5rem", render: (r) => termsText(r.termsDays) },
    { key: "limit", header: "Limit", numeric: true, sortable: true, width: "8.5rem", render: (r) => (r.limitCents === null ? "No limit" : money(r.limitCents)) },
    { key: "open", header: "Open balance", numeric: true, sortable: true, width: "9rem", render: (r) => money(r.openCents) },
    { key: "left", header: "Available", numeric: true, sortable: true, width: "8.5rem", render: (r) => (r.availableCents === null ? "-" : <span className={r.availableCents === 0 ? "ffa-warn" : undefined}>{money(r.availableCents)}</span>) },
    ...(can("credit:decide") ? [{ key: "act", header: "Action", width: "6rem", render: (r: CreditLimit) => <Button size="sm" onClick={() => setEditing(r)}>Change</Button> }] : []),
  ];
  const atLimit = (limits.data?.customers ?? []).filter((r) => r.availableCents === 0).length;
  const toolbar = (
    <FilterToolbar
      label={tab === "applications" ? "Application filters" : "Limit filters"}
      search={<SearchInput label={tab === "applications" ? "Search customer, legal name or AP email" : "Search customer"} value={search} onChange={setSearch} />}
      items={tab === "applications" ? [{ key: "status", priority: 1, active: status !== "pending", inline: <Select variant="toolbar" label="Status" value={status} onChange={(s) => f.set({ status: s })} options={STATUSES.map((s) => ({ value: s, label: statusBadge("creditApplication", s).label }))} /> }] : []}
      anyActive={v.q !== "" || (tab === "applications" && status !== "pending")}
      showReset
      onReset={() => {
        setSearch("");
        f.set({ q: "", status: "pending" });
      }}
    />
  );
  return (
    <section className="ffa-page">
      <PageHeader title="Credit" subtitle="Net terms applications from customers, and every customer's limit with what's open against it. Bookings over a limit are refused." />
      <Tabs
        idBase={TAB_ID}
        label="Credit sections"
        value={tab}
        onChange={(t) => {
          setSearch("");
          f.set({ tab: t, status: "pending", q: "", sort: "", dir: "asc" });
        }}
        tabs={[
          { value: "applications", label: "Applications", ...(apps.data && tab === "applications" ? { count: apps.data.applications.length } : {}) },
          { value: "limits", label: "Limits", ...(limits.data ? { count: limits.data.customers.length } : {}) },
        ]}
      />
      <TabPanel idBase={TAB_ID} value={tab} className="ffa-tabpanel">
        {tab === "applications" ? (
          <>
            {toolbar}
            <DataTable
              caption="Credit applications"
              columns={appCols}
              rows={appRows}
              rowKey={(a) => a.id}
              loading={apps.isPending || apps.isFetching}
              error={apps.isError ? { title: "Couldn't load applications", message: message(apps.error), onRetry: () => void apps.refetch() } : null}
              empty={v.q ? "No applications match." : status === "pending" ? "No applications waiting." : "None."}
              sort={sort}
              onSortChange={(s) => f.set(sortPatch(s))}
              onRowClick={setOpen}
            />
          </>
        ) : (
          <>
            <div className="ffa-kpis">
              <KpiTile label="On terms" value={limits.data ? String(limits.data.customers.length) : "–"} sub="customers" />
              <KpiTile label="At their limit" value={limits.data ? String(atLimit) : "–"} sub="can't book more" tone={atLimit > 0 ? "danger" : undefined} />
            </div>
            {toolbar}
            <DataTable
              caption="Credit limits"
              columns={limitCols}
              rows={limitRows}
              rowKey={(r) => r.orgId}
              loading={limits.isPending || limits.isFetching}
              error={limits.isError ? { title: "Couldn't load credit limits", message: message(limits.error), onRetry: () => void limits.refetch() } : null}
              empty={v.q ? "No customers match." : "No customers on credit terms yet."}
              sort={sort}
              onSortChange={(s) => f.set(sortPatch(s))}
              rowTone={(r) => (r.availableCents === 0 ? "warning" : null)}
            />
          </>
        )}
      </TabPanel>
      {open ? <ApplicationDialog app={open} canDecide={can("credit:decide")} onClose={() => setOpen(null)} /> : null}
      {editing ? <LimitDialog row={editing} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function ApplicationDialog({ app, canDecide, onClose }: { app: CreditApplication; canDecide: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CreditDecisionForm>({ decision: "approve", terms: String(app.requestedTermsDays), limit: String(app.requestedLimitCents / 100), note: "" });
  const [errors, setErrors] = useState<Partial<Record<"terms" | "limit" | "note", string>>>({});
  const pending = app.status === "pending" && canDecide;
  const decide = useMutation({
    mutationFn: (body: object) => api<CreditApplication & { customerEmail: "queued" | "not_connected" | "not_sent" }>(`/v1/admin/credit-applications/${app.id}/decision`, { method: "POST", body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "credit"] });
      notify.success(r.status === "approved" ? `Approved: ${termsText(r.decidedTermsDays)}, ${wholeDollars(r.decidedLimitCents ?? 0)}` : "Application rejected", emailOutcomeText(r.customerEmail));
      onClose();
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const submit = () => {
    const r = checkCreditDecision(form);
    setErrors(r.errors);
    if (r.body) decide.mutate(r.body);
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={app.legalName}
      description={`${app.orgName ?? ""} · applied ${day(app.createdAt)}`}
      footer={
        pending ? (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant={form.decision === "reject" ? "destructive" : "primary"} loading={decide.isPending} onClick={submit}>{form.decision === "approve" ? "Approve terms" : "Reject"}</Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="ffa-form">
        <DefinitionList
          items={[
            { term: "Status", detail: <StatusBadge domain="creditApplication" value={app.status} /> },
            { term: "Asked for", detail: `${termsText(app.requestedTermsDays)}, ${wholeDollars(app.requestedLimitCents)} limit` },
            { term: "Monthly volume", detail: app.monthlyVolumeCents === null ? "Not given" : wholeDollars(app.monthlyVolumeCents) },
            { term: "AP email", detail: app.apEmail },
            { term: "Trade references", detail: app.tradeReferences ?? "None given" },
            app.notes ? { term: "Notes", detail: app.notes } : null,
            app.decidedAt
              ? { term: "Decision", detail: `${statusBadge("creditApplication", app.status).label} ${day(app.decidedAt)}${app.decidedLimitCents !== null ? `: ${termsText(app.decidedTermsDays)}, ${wholeDollars(app.decidedLimitCents)}` : ""}${app.decisionNote ? `. ${app.decisionNote}` : ""}` }
              : null,
          ]}
        />
        {pending ? (
          <>
            <Select label="Decision" value={form.decision} onChange={(d) => setForm({ ...form, decision: d as "approve" | "reject" })} options={[{ value: "approve", label: "Approve net terms" }, { value: "reject", label: "Reject" }]} />
            {form.decision === "approve" ? (
              <div className="ffx-fields">
                <Select label="Payment terms" value={form.terms} onChange={(t) => setForm({ ...form, terms: t })} options={termOptions} error={errors.terms} />
                <TextField label="Credit limit" prefix="$" inputMode="decimal" value={form.limit} onChange={(e) => setForm({ ...form, limit: e.target.value })} error={errors.limit} hint="May differ from what they asked for." />
              </div>
            ) : null}
            <TextAreaField label="Note to the customer" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} maxLength={CREDIT_NOTE_MAX} optional={form.decision === "approve"} error={errors.note} />
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function LimitDialog({ row, onClose }: { row: CreditLimit; onClose: () => void }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [terms, setTerms] = useState(row.termsDays === null ? "prepay" : String(row.termsDays));
  const [limit, setLimit] = useState(row.limitCents === null ? "" : String(row.limitCents / 100));
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: object) => api<CreditLimit>(`/v1/admin/orgs/${row.orgId}/credit`, { method: "PUT", body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["console", "credit"] });
      notify.success(r.limitCents === null ? `${row.orgName} is on prepay` : `${row.orgName}: ${termsText(r.termsDays)}, ${money(r.limitCents)} limit`, "No email is sent for this change.");
      onClose();
    },
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const submit = async () => {
    if (terms === "prepay") {
      if (await confirm({ title: `Put ${row.orgName} on prepay?`, body: "Their net terms and credit limit are removed.", confirmLabel: "Put on prepay", danger: true })) save.mutate({ termsDays: null, limitCents: null });
      return;
    }
    const l = parseLimit(limit);
    setErr(l.error ?? null);
    if (l.cents === undefined) return;
    if (l.cents < row.openCents && !(await confirm({ title: `Set the limit below what's open?`, body: `${row.orgName} has ${money(row.openCents)} open, so new bookings are refused until they pay down below ${money(l.cents)}.`, confirmLabel: "Set limit" }))) return;
    save.mutate({ termsDays: Number(terms), limitCents: l.cents });
  };
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={`Credit for ${row.orgName}`}
      description={`Open balance ${money(row.openCents)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>Save</Button>
        </>
      }
    >
      <div className="ffa-form">
        <Select label="Payment terms" value={terms} onChange={setTerms} options={[...termOptions, { value: "prepay", label: "Prepay (no terms)" }]} />
        {terms !== "prepay" ? <TextField label="Credit limit" prefix="$" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value)} error={err ?? undefined} /> : null}
      </div>
      {dialog}
    </Modal>
  );
}
